import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import format from 'string-format';
import _path from 'ramda/src/path';
import Avatar from '../Avatar';
import Image from '../Image';
import Button from '../Button';
import DateFromNow from '../DateFromNow';
import DeleteComment from './DeleteComment';
import { withMessage } from '../Message';
import resx from '../../../utilities/resx';
import generalUtilities from '../../../utilities/generalUtility';
import searchComment from '../../../API/SearchComment';
import loggly from '../../../API/Loggly';
import { RESX_GLOBAL, RESX_REBLASTS, LOGGLY_LEVEL_ERROR } from '../../../Constants';

function CommentsView(props) {
    if (!_path(['RESAAS', 'User2', 'IsProfessional'], window)) {
        return null;
    }
    const lazy = props.lazy;
    const state = props.data;
    const {
        Comments,
        TotalCommentCount,
        commentsFetch,
        commentsDisplayCount,
        commentsRequestedByUser,
        commentsPendingId,
        hasMoreCommentsToShow
    } = props.data;
    const { setState } = props;
    const [loading, setLoading] = useState(false);
    const applicationUrl = _path(['RESAAS', 'Environment', 'ApplicationUrl'], window);
    let loadingTimeout;

    const sortComments = comments => {
        return [...comments].sort((a, b) => {
            const dateA = new Date(a.DateCreatedFormatted);
            const dateB = new Date(b.DateCreatedFormatted);
            return dateB - dateA;
        });
    };

    const handleReplyClick = usermention => {
        props.textareaRef.current.click();
        props.setInputValue(`@${usermention.replace(/^\//, '')} `);
        props.textareaRef.current.focus();
    };

    useEffect(
        () => {
            const hasMoreComments =
                Comments && (Comments.length > commentsDisplayCount || TotalCommentCount > commentsDisplayCount);
            setState(prevState => ({ ...prevState, hasMoreCommentsToShow: hasMoreComments }));
        },
        [hasMoreCommentsToShow]
    );

    const fetchComments = () => {
        loadingTimeout = setTimeout(() => {
            setLoading(true);
        }, 1000);
        searchComment
            .browse({
                criteria: {
                    ContentId: state.ReblastId,
                    RecordCount: state.commentsDisplayCount === Infinity ? 100 : state.commentsDisplayCount + 1
                }
            })
            .then(response => {
                const result = _path(['data', 'SearchCommentResult'], response);
                if (!result || !result.Success) {
                    throw new Error(result.Message);
                }
                setState(prevState => ({ ...prevState, Comments: sortComments(result.Data) }));
            })
            .catch(error => {
                props.message.addMessage({
                    message: resx(RESX_GLOBAL, 'CANNOT_RETRIEVE_COMMENTS_ERROR'),
                    type: 'error'
                });
                loggly.add(LOGGLY_LEVEL_ERROR, `Reblast comments could not be retrieved: ${error}`);
            })
            .finally(() => {
                clearTimeout(loadingTimeout);
                setLoading(false);
            });
    };

    useEffect(
        () => {
            if (commentsFetch) {
                fetchComments();
            }
        },
        [commentsFetch, commentsDisplayCount, commentsPendingId]
    );

    const handleLoadMoreClick = () => {
        setState(prevState => ({
            ...prevState,
            commentsFetch: true,
            commentsDisplayCount: Infinity,
            commentsRequestedByUser: false,
            hasMoreCommentsToShow: false
        }));
    };

    const loadMoreText = () => {
        const remainingComments = TotalCommentCount - commentsDisplayCount;

        if (remainingComments === 1) {
            return resx(RESX_REBLASTS, 'VIEW_ONE_MORE_COMMENT');
        }

        return format(resx(RESX_REBLASTS, 'VIEW_MORE_COMMENTS'), remainingComments);
    };

    const showCTA = () => Comments && Comments.length === 0 && commentsRequestedByUser;
    const showDelete = comment => comment.WebsiteId === RESAAS.User2.WebsiteId || comment.IsOwner;
    const displayName = comment => `${comment.FirstName} ${comment.LastName}`;
    const formatText = comment => ({ __html: generalUtilities.linkifyTextString(comment.Text) });

    return (
        <React.Fragment>
            {showCTA() && (
                <div className="re-comment-cta" data-testid="comments-cta">
                    {resx(RESX_REBLASTS, 'NO_NEW_COMMENTS')}
                </div>
            )}
            <ul className="comment-list-wrapper">
                {Comments &&
                    Comments.slice(0, commentsDisplayCount).map(comment => (
                        <li key={comment.CommentId} className="comment-list-item">
                            <a
                                href={`${applicationUrl}/${comment.DomainUrl}/profile/`}
                                className="comment-user-photo-wrapper display-block"
                            >
                                <Avatar
                                    displayName={displayName(comment)}
                                    size="xxs"
                                    lazy={lazy}
                                    src={comment.UserProfilePictureUrl}
                                    websiteId={comment.WebsiteId}
                                />
                            </a>
                            <div className="comment-content-wrapper">
                                <ul className="list-inline comment-item-user-info">
                                    <li className="comment-item-username text-bold">
                                        <a
                                            href={`${applicationUrl}/${comment.DomainUrl}/profile/`}
                                            className="comment-name"
                                        >
                                            {displayName(comment)}
                                        </a>
                                        <span className="comment-content-userdomain">
                                            @{comment.DomainUrl.replace(/^\//, '')}
                                        </span>
                                    </li>
                                    <li className="comment-item-meta">
                                        {showDelete(comment) ? (
                                            <DeleteComment commentId={comment.CommentId} setState={setState} />
                                        ) : null}
                                    </li>
                                </ul>
                                <div
                                    className="comment-item-content comment-text"
                                    dangerouslySetInnerHTML={formatText(comment)}
                                />
                                <div className="comment-text-details">
                                    <div className="comment-date-info">
                                        <DateFromNow date={comment.DateCreatedFormatted} />
                                    </div>
                                    <button
                                        className="re-inlinebutton re-icon re-icon-xs re-icon-counterclockwise comment-reply-btn"
                                        onClick={() => handleReplyClick(comment.DomainUrl)}
                                    >
                                        <span>Reply</span>
                                    </button>
                                </div>
                                {comment.Attachment &&
                                    comment.Attachment.AttachmentImage && (
                                        <div className="comment-attachment comment-attachment-photo">
                                            <a href={comment.Attachment.AttachmentImage[1].Url} rel="prettyPhoto">
                                                <Image
                                                    src={`${comment.Attachment.AttachmentImage[1].Url}`}
                                                    className="bg-attachment-photo"
                                                    width={536}
                                                    quality={80}
                                                    lazy={lazy}
                                                />
                                            </a>
                                        </div>
                                    )}
                                {comment.Attachment &&
                                    comment.Attachment.AttachmentDocument && (
                                        <div className="comment-attachment comment-attachment-document">
                                            <a
                                                href={comment.Attachment.AttachmentDocument.Url}
                                                target="_blank"
                                                rel="noopener noreferrer prettyPhoto"
                                            >
                                                <div className="bg-attachment-document" />
                                                <span>{resx(RESX_GLOBAL, 'VIEW_PDF_TEXT')}</span>
                                            </a>
                                        </div>
                                    )}
                            </div>
                        </li>
                    ))}
            </ul>
            {hasMoreCommentsToShow ? (
                <Button
                    className="re-btn re-btn-small re-btn-cancel re-btn-full-width re-comment-loading-more-btn"
                    dataTestid="comments-loadmore"
                    text={loadMoreText()}
                    onClick={() => handleLoadMoreClick()}
                />
            ) : null}
            {loading ? (
                <React.Fragment>
                    <li className="re-comment-placholder-item loading">
                        <div className="mediaItem mediaItem-verticalCenter">
                            <div className="mediaItem-media">
                                <div className="re-avatar-xxs re-avatar-placeholder loading-animation" />
                            </div>
                            <div className="mediaItem-body">
                                <div className="re-comment-placholder-item-name loading-animation" />
                                <div className="re-comment-placholder-item-comment loading-animation" />
                            </div>
                        </div>
                    </li>
                    <li className="re-comment-placholder-item loading">
                        <div className="mediaItem mediaItem-verticalCenter">
                            <div className="mediaItem-media">
                                <div className="re-avatar-xxs re-avatar-placeholder loading-animation" />
                            </div>
                            <div className="mediaItem-body">
                                <div className="re-comment-placholder-item-name loading-animation" />
                                <div className="re-comment-placholder-item-comment loading-animation" />
                            </div>
                        </div>
                    </li>
                    <li className="re-comment-placholder-item loading">
                        <div className="mediaItem mediaItem-verticalCenter">
                            <div className="mediaItem-media">
                                <div className="re-avatar-xxs re-avatar-placeholder loading-animation" />
                            </div>
                            <div className="mediaItem-body">
                                <div className="re-comment-placholder-item-name loading-animation" />
                                <div className="re-comment-placholder-item-comment loading-animation" />
                            </div>
                        </div>
                    </li>
                </React.Fragment>
            ) : null}
        </React.Fragment>
    );
}

CommentsView.propTypes = {
    data: PropTypes.object.isRequired,
    lazy: PropTypes.bool,
    message: PropTypes.object,
    setState: PropTypes.func.isRequired
};

export default withMessage(CommentsView);
